/**
 * The builder's chrome, restyled onto EMERGE's tokens.
 *
 * GrapesJS ships its own stylesheet (`grapesjs/dist/css/grapes.min.css`,
 * imported once by `EmailBuilder.tsx`) and drives nearly every colour in it
 * through `--gjs-*` custom properties declared on `:root`. Redeclaring those
 * properties on `.emerge-builder` re-skins the whole editor — panels, fields,
 * layer manager, modals, the rich-text toolbar — without fighting its
 * selectors one by one, and it follows light and dark for free because every
 * value here is a token.
 *
 * ⚠️ THE RAW TOKENS (`--surface`, `--fg`, …), NOT `--color-*`. `src/index.css`
 * declares the palette inside `@theme inline`, which inlines the values into
 * Tailwind utilities and does not reliably emit the `--color-*` properties at
 * runtime. The raw tokens are declared on `:root` and swapped under
 * `[data-theme='dark']`, so they are the ones a stylesheet can read.
 *
 * ⚠️ NOTHING HERE TOUCHES THE CANVAS CONTENT. The email inside the iframe is
 * the recipient's document, with its own literal colours; only the editor
 * around it follows the app's theme.
 *
 * The side panel is widened from GrapesJS's 15% to a fixed width: at 1280px a
 * percentage leaves the style manager about 170px wide, too narrow for its
 * two-up property rows.
 */

/** Width of the right-hand panel (blocks / styles / traits / layers). */
const SIDE = '288px';

export const BUILDER_THEME_CSS = `
.emerge-builder {
  --gjs-main-color: var(--surface);
  --gjs-primary-color: var(--surface);
  --gjs-secondary-color: var(--fg-secondary);
  --gjs-tertiary-color: var(--accent-brand);
  --gjs-quaternary-color: var(--accent-brand);
  --gjs-font-color: var(--fg-secondary);
  --gjs-font-color-active: var(--fg);
  --gjs-main-dark-color: var(--surface-sunken);
  --gjs-secondary-dark-color: var(--surface-hover);
  --gjs-main-light-color: var(--surface-hover);
  --gjs-secondary-light-color: var(--fg-muted);
  --gjs-soft-light-color: var(--surface-sunken);
  --gjs-light-border: var(--hairline);
  --gjs-arrow-color: var(--fg-muted);
  --gjs-dark-text-shadow: transparent;
  --gjs-color-highlight: var(--accent-brand);
  --gjs-color-blue: var(--accent-brand);
  --gjs-color-red: var(--danger);
  --gjs-color-yellow: var(--warning);
  --gjs-color-warn: var(--warning);
  --gjs-color-green: var(--success);
  --gjs-main-font: inherit;
  --gjs-font-size: 0.75rem;
  --gjs-left-width: ${SIDE};
}
.emerge-builder .gjs-editor { font-family: inherit; color: var(--fg-secondary); background-color: var(--canvas); }
.emerge-builder .gjs-cv-canvas { background-color: var(--surface-sunken); }
.emerge-builder .gjs-pn-panel { border-color: var(--hairline); }
.emerge-builder .gjs-pn-commands,
.emerge-builder .gjs-pn-views-container { box-shadow: none; }
.emerge-builder .gjs-pn-commands { border-bottom: 1px solid var(--hairline); }
.emerge-builder .gjs-pn-views-container { border-left: 1px solid var(--hairline); }
.emerge-builder .gjs-pn-views { border-bottom: 1px solid var(--hairline); border-left: 1px solid var(--hairline); }
.emerge-builder .gjs-pn-btn { color: var(--fg-muted); border-radius: var(--radius-sm, 4px); }
.emerge-builder .gjs-pn-btn:hover { color: var(--fg); background-color: var(--surface-hover); }
.emerge-builder .gjs-pn-btn.gjs-pn-active { color: var(--fg); background-color: var(--surface-active); box-shadow: none; }
.emerge-builder .gjs-pn-btn:focus-visible { outline: 2px solid var(--focus-ring); outline-offset: 1px; }
.emerge-builder .gjs-four-color,
.emerge-builder .gjs-four-color-h:hover { color: var(--fg); }
.emerge-builder .gjs-field,
.emerge-builder .gjs-input-holder input,
.emerge-builder .gjs-field input,
.emerge-builder .gjs-field select,
.emerge-builder .gjs-field textarea { background-color: var(--surface); color: var(--fg); }
.emerge-builder .gjs-field { border: 1px solid var(--hairline); border-radius: var(--radius-sm, 4px); }
.emerge-builder .gjs-block {
  color: var(--fg-secondary);
  background-color: var(--surface);
  border: 1px solid var(--hairline);
  border-radius: var(--radius-md, 6px);
  box-shadow: none;
  min-height: 64px;
  font-weight: 500;
}
.emerge-builder .gjs-block:hover { color: var(--fg); border-color: var(--hairline-strong); }
.emerge-builder .gjs-block-category .gjs-title,
.emerge-builder .gjs-sm-sector-title,
.emerge-builder .gjs-layer-title,
.emerge-builder .gjs-trt-header {
  background-color: var(--surface-sunken);
  color: var(--fg-secondary);
  border-bottom: 1px solid var(--hairline);
  font-weight: 600;
}
.emerge-builder .gjs-layer { color: var(--fg-secondary); }
.emerge-builder .gjs-layer.gjs-selected .gjs-layer-item { background-color: var(--surface-active); color: var(--fg); }
.emerge-builder .gjs-mdl-dialog { background-color: var(--surface-raised); color: var(--fg); border: 1px solid var(--hairline); border-radius: var(--radius-lg, 8px); }
.emerge-builder .gjs-mdl-header { border-bottom: 1px solid var(--hairline); }
.emerge-builder .gjs-mdl-container { background-color: var(--scrim); }
.emerge-builder .gjs-rte-toolbar { background-color: var(--surface-raised); border: 1px solid var(--hairline); border-radius: var(--radius-md, 6px); }
.emerge-builder .gjs-rte-action { color: var(--fg-secondary); border-right-color: var(--hairline); }
.emerge-builder .gjs-rte-active { background-color: var(--surface-active); color: var(--fg); }
.emerge-builder .emerge-rte-variable {
  font: inherit;
  font-size: 12px;
  color: var(--fg);
  background-color: var(--surface);
  border: 1px solid var(--hairline);
  border-radius: var(--radius-sm, 4px);
  padding: 2px 4px;
  max-width: 160px;
}
.emerge-builder .gjs-am-assets-cont,
.emerge-builder .gjs-am-file-uploader > form { background-color: var(--surface-sunken); }
.emerge-builder .gjs-am-file-uploader > form { border: 1px dashed var(--hairline-strong); color: var(--fg-muted); }
.emerge-builder .gjs-am-asset { border-bottom: 1px solid var(--hairline); }
.emerge-builder .gjs-off-prv { background-color: var(--surface-raised); color: var(--fg); }

/* Read only: the email is shown, nothing around it can be used to change it. */
.emerge-builder--readonly .gjs-pn-views,
.emerge-builder--readonly .gjs-pn-views-container,
.emerge-builder--readonly .gjs-pn-options { display: none !important; }
.emerge-builder--readonly .gjs-cv-canvas,
.emerge-builder--readonly .gjs-pn-commands { width: 100%; }
`;
